import type { ContentAvailability } from "@-/dev.improve.haniverse.lib.database.shards";
import { ActorDatabaseContext } from "@-/dev.improve.haniverse.contexts.database.actor";
import { MINIMUM_REVIEW_INTERVAL_MS } from "@-/dev.improve.haniverse.lib.cards.user-card.fsrs";
import {
  CARD_CONTENT_OVERLAY_TABLES,
  type CardContentKind,
  type CardReviewSettings,
  type CardSubject,
  isCardContentAvailable,
  isCardContentKindReviewEligible,
  resolveUserCardContentKind,
  resolveUserCardSubject,
} from "@-/dev.improve.haniverse.lib.cards.card-content";
import {
  tableNameMatcher,
  useTableChangeVersion,
} from "@-/dev.improve.haniverse.lib.database.change";
import {
  isCardContentKindInReviewFocus,
  type ReviewFocus,
} from "@-/dev.improve.haniverse.lib.learn.review-focus";
import {
  isCardContentKindAllowedInStudyEnvironment,
  type StudyEnvironment,
} from "@-/dev.improve.haniverse.lib.learn.study-environment";
import { useDailyNewGraphemeAllowanceQueryOptions } from "@-/dev.improve.haniverse.lib.learn.new-grapheme-limit";
import { createUseQueryOptions } from "@-/dev.improve.haniverse.lib.query-options";
import { useContextSelectors } from "@-/com.npmjs.use-context-selectors";

/**
 * One in-study `user_card` whose FSRS schedule is due now or in the past, resolved from the ACTOR
 * database alone — no content-DB read, no per-card query.
 *
 * `cardContentKind` / `subject` come straight from which overlay row the card carries (see
 * `resolveUserCardContentKind`). A card whose overlay has not synced yet has neither: consumers
 * decide what that means for them (the due-count badge counts it, the scheduler skips it).
 */
export type DueUserCard = {
  cardContentKind: CardContentKind | null;
  /** FSRS `due` as stored (ISO). */
  due: string;
  /** `due` as epoch milliseconds — the sort key, precomputed once. */
  dueMs: number;
  /** What the card is about, or `null` when no overlay is present. */
  subject: CardSubject | null;
  userCardId: string;
};

// `user` carries the Daily New Cards limit, which decides whether just-introduced cards wait.
const DUE_USER_CARDS_TABLES = [
  "user",
  "user_card",
  "fsrs_card",
  ...CARD_CONTENT_OVERLAY_TABLES,
] as const;

/**
 * The query key for the due-card scan. Registered against every table the scan reads, so a rating,
 * a new card, or an overlay sync invalidates it — freshness comes from invalidation, exactly like
 * the `actorQueries` reads it replaces.
 *
 * `liftIntroductionHold` revives recently learned cards whatever today's allowance says: the deck
 * plans its next cards before the lesson that spends the allowance has been accepted.
 */
export function useGetDueUserCardsQueryKey() {
  const [actorDb] = useContextSelectors(ActorDatabaseContext, (c) => c.db);
  const dailyNewGraphemeAllowanceQueryOptions = useDailyNewGraphemeAllowanceQueryOptions();
  const withTableChangeVersion = useTableChangeVersion(tableNameMatcher(DUE_USER_CARDS_TABLES));
  return ({ liftIntroductionHold = false }: { liftIntroductionHold?: boolean } = {}) =>
    withTableChangeVersion([
      "dueUserCards",
      { actorDb, dailyNewGraphemeAllowanceQueryOptions, liftIntroductionHold },
    ] as const);
}

/**
 * Every due in-study card, sorted by earliest `due` first.
 *
 * This is THE due-review read: the summary's count and glyph grid, the review header's badge, and
 * the next-due scheduler all derive from it, so they share one cache entry and one SQL scan.
 *
 * Why a direct actor read rather than an `actorQueries` config: the due cutoff is `now`, which has
 * to be evaluated at FETCH time (a cached config would freeze it at render), and it is applied IN
 * SQL — `fsrs_card.due <= now` filters through the relation instead of loading every in-study
 * card into JS. The previous shape loaded the whole deck joined to four relations and filtered in
 * JS, then fetched a content-DB app card PER CARD to learn what each was about; with a few
 * thousand cards that was tens of seconds on the JS thread (see the perf tests under
 * `testing/perf`). The overlays are joined with narrow column projections so the row count, not
 * the row width, is the only thing that scales.
 *
 * A never-reviewed card normally waits {@link MINIMUM_REVIEW_INTERVAL_MS} from its introduction,
 * so it does not interrupt the short unit that introduced it. Once today's new-grapheme allowance
 * is spent (`useDailyNewGraphemeAllowanceQueryOptions`) no unit follows, so those recently learned
 * cards are revived: due at once, as reviews. A session that reaches the daily limit therefore
 * ends on reviewing what it just learned, never on the limit itself.
 */
export const [useDueUserCardsQueryOptions, dueUserCardsQueryFn] = createUseQueryOptions(
  useGetDueUserCardsQueryKey,
  async ({
    client,
    queryKey: [_, { actorDb, dailyNewGraphemeAllowanceQueryOptions, liftIntroductionHold }],
  }): Promise<DueUserCard[]> => {
    const allowance = liftIntroductionHold
      ? null
      : await client.fetchQuery(dailyNewGraphemeAllowanceQueryOptions);
    const isLimitReached = liftIntroductionHold || allowance?.isLimitReached === true;
    const now = Date.now();
    const nowIso = new Date(now).toISOString();
    const reviewCutoff = new Date(now - MINIMUM_REVIEW_INTERVAL_MS).toISOString();
    // oxlint-disable-next-line han-actor-db/no-raw-actor-db-in-query-fn -- the `due <= now` cutoff is a fetch-time value, see above
    const rows = await actorDb(import.meta.location).query.userCard.findMany({
      columns: { id: true },
      where: {
        fsrsCard: { due: { lte: nowIso } },
        fsrsCardId: { isNotNull: true },
        // Apply this to existing schedules too, including cards synced from older clients.
        // Never-reviewed cards wait from introduction instead of interrupting a short unit,
        // unless the daily limit means no unit follows (see above).
        OR: [
          { fsrsCard: { lastReview: { lte: reviewCutoff } } },
          isLimitReached
            ? { fsrsCard: { lastReview: { isNull: true } } }
            : {
                fsrsCard: { lastReview: { isNull: true } },
                startedStudyingAt: { lte: reviewCutoff },
              },
        ],
      },
      with: {
        fsrsCard: { columns: { due: true } },
        userGraphemeMeaningCard: { columns: { grapheme: true } },
        userGraphemePronunciationCard: { columns: { grapheme: true } },
        userQuizCard: { columns: { key: true } },
        userWordSentenceCard: { columns: { kind: true, word: true } },
      },
    });

    const dueUserCards: DueUserCard[] = [];
    for (const row of rows) {
      const due = row.fsrsCard?.due;
      if (due === undefined) {
        continue;
      }
      const dueMs = new Date(due).getTime();
      if (Number.isNaN(dueMs)) {
        continue;
      }
      const cardContentKind = resolveUserCardContentKind(row);
      dueUserCards.push({
        cardContentKind,
        due,
        dueMs,
        subject: resolveUserCardSubject(row),
        userCardId: row.id,
      });
    }
    dueUserCards.sort((a, b) => a.dueMs - b.dueMs);
    return dueUserCards;
  },
);

/** A due card with a known kind that passes the review-eligibility gates. */
export type ReviewEligibleDueUserCard = DueUserCard & {
  cardContentKind: CardContentKind;
  subject: CardSubject;
};

/**
 * The due cards the scheduler may actually serve under the user's current settings, study
 * environment and review focus, in due order. Drops cards with no synced overlay (nothing is known
 * about them), kinds the settings gate out (pronunciation cards while pinyin flashcards are off, …),
 * kinds the environment cannot study (mic drills during Silent Study, …) and kinds outside the
 * focus (everything but pronunciation cards while practicing pronunciation, …), and cards the active
 * content catalog cannot show (`isCardContentAvailable`: grapheme cards outside the free tier in
 * offline mode).
 */
export function filterReviewEligibleDueUserCards(
  dueUserCards: readonly DueUserCard[],
  settings: CardReviewSettings,
  {
    contentAvailability,
    reviewFocus,
    studyEnvironment,
  }: {
    contentAvailability: ContentAvailability;
    reviewFocus: ReviewFocus;
    studyEnvironment: StudyEnvironment;
  },
): ReviewEligibleDueUserCard[] {
  const eligible: ReviewEligibleDueUserCard[] = [];
  for (const card of dueUserCards) {
    const { cardContentKind, subject } = card;
    if (cardContentKind === null || subject === null) {
      continue;
    }
    if (
      !isCardContentKindReviewEligible({ cardContentKind, settings }) ||
      !isCardContentKindAllowedInStudyEnvironment({ cardContentKind, studyEnvironment }) ||
      !isCardContentKindInReviewFocus({ cardContentKind, reviewFocus }) ||
      !isCardContentAvailable({ cardContentKind, contentAvailability, subjectText: subject.text })
    ) {
      continue;
    }
    eligible.push({ ...card, cardContentKind, subject });
  }
  return eligible;
}
